import {
  BILLED_INVOICE_STATUSES,
  type InvoiceStatus,
  type Unit,
  type ValidationStatus,
} from '../types/enums.js';
import { addDays, daysBetween } from '../format/date.js';
import { BILLABLE_CR_CLASSIFICATION } from './contract.js';
import { lineTotal, round2, sumMoney } from './money.js';
import { calculateVat, type VatResult } from './vat.js';

/**
 * Faktureringsmodellen: timer grupperes pr. opgave til én fakturalinje med opgavetitlen
 * som tekst. Scope-beslutning 2 og 3, PRD §7.1-7.2.
 *
 * Funktionerne her er rene. De laeser ingen database og kender ingen HTTP - derfor kan
 * bade API'ets generering og frontendens preview kalde praecis samme kode og aldrig
 * naa forskellige tal.
 */

/** En time som den ser ud naar faktureringsgrundlaget skal beregnes. */
export interface CandidateHour {
  id: string;
  task_id: string | null;
  /** Aktuel titel fra tasks.title. Snapshottes paa linjen ved generering. */
  task_title: string | null;
  duration_hours: number;
  /**
   * The hours to invoice on a service agreement task - `todos.invoice_hours`, picked by the
   * developer in OnePro. Null or left out: the used hours. Only `groupHoursForServiceAgreement`
   * reads it.
   */
  invoice_hours?: number | null;
  hourly_rate_excl_vat: number | null;
  validation_status: ValidationStatus;
  /** True hvis timen allerede har et invoice_billing_items-grundlag. */
  already_billed: boolean;
  /**
   * Kontraktlinjen opgaven hoerer til, raat fra `todos.contract_line`.
   *
   * Fri tekst i kilden, og udfyldt paa et mindretal af raekkerne - se
   * `normalizeContractLine`, der er det ene sted formen tolkes.
   */
  contract_line?: string | null;
  /**
   * Opgaven kommer fra en aendringsanmodning - `todos.source = 'Change Request'`.
   *
   * To ting foelger af den: timen grupperes under sin egen fakturalinje frem for under en
   * kontraktlinje, og den er den eneste slags, der kan goeres gratis. Se
   * `CHANGE_REQUEST_LINE_LABEL` og `freeChangeRequestShares`.
   */
  is_change_request?: boolean;
  /**
   * `orchestrator.change_requests.classification` paa den anmodning, opgaven kom af.
   *
   * **Porten til at opkraeve for en aendring.** Kun `out_of_scope` er arbejde uden for det
   * aftalte og dermed penge; `in_scope_clarification` og `grey_zone` er daekket af aftalen i
   * forvejen. Se `isChargeableChangeRequest`, som er det ene sted reglen staar.
   *
   * Null eller tom naar anmodningen ikke kunne slaas op - der findes ingen noegle fra
   * `todos` til `change_requests`, saa koblingen sker paa titel inden for aftalens projekter,
   * se demo/change-request-classification.ts i API'et. Uden svar opkraeves der ikke.
   *
   * Uden betydning paa en time der ikke er en aendringsanmodning.
   */
  cr_classification?: string | null;
  /**
   * Opgavens estimat - `todos.time_estimate`, som allerede staar i **timer**.
   *
   * Med for at kunne vises ved siden af forbruget paa /fakturering/ny: en buffer laegger timer
   * oven paa de brugte, og uden estimatet kan controlleren ikke se, at fakturaen dermed kommer
   * over det, opgaven var sat til. Null naar ingen har sat et estimat - og det er ikke nul
   * timer, saa det maa ikke blive til 0.
   *
   * `total_time` er sekunder og `time_estimate` er timer. Kun den ene skal regnes om; se
   * `onepro-schemas-and-units`.
   */
  estimate_hours?: number | null;
}

/**
 * Vaerdien i `todos.source`, naar opgaven er oprettet af en aendringsanmodning.
 *
 * Orchestrators egen streng, ikke Codepass'. Den staar som konstant ét sted, fordi baade
 * fakturagrundlaget og en kommende regel afgraenser paa den, og en stavefejl ville give en
 * faktura, hvor aendringerne stille blev til almindelige timer.
 */
export const CHANGE_REQUEST_TODO_SOURCE = 'Change Request';

/**
 * The value in `todos.source` when the task is work under the customer's service agreement.
 *
 * Set in OnePro, not by Codepass. Such work is in no contract plan and is not a change request -
 * the service agreement is its basis - so the rules that hold a board up against the plan leave
 * it alone, and the invoice puts it under the customer's service agreement wherever its board
 * is: see `splitHoursByContract` in the API.
 */
export const SERVICE_AGREEMENT_TODO_SOURCE = 'Serviceaftale';

/**
 * Does the task count among the agreement's hours - the Timer tab, 'Timer af loft', the contract
 * list and Kontraktloft?
 *
 * **Each task belongs to exactly one side.** Decided 14-10-2026:
 *
 *   project agreement   everything on its board except service agreement work - contract lines
 *                       ('Kontrakt'), work created in OnePro ('OnePro') and change requests
 *   service agreement   only the tasks marked 'Serviceaftale' - see `SERVICE_AGREEMENT_TODO_SOURCE`
 *
 * A service agreement can share a board with the original project, and its page must not list
 * the project's work as well. A task without the mark on the service agreement's board is the
 * project's.
 */
export function countsAsContractHours(
  task: { source?: string | null },
  serviceAgreement: boolean,
): boolean {
  return (task.source === SERVICE_AGREEMENT_TODO_SOURCE) === serviceAgreement;
}

/**
 * Maa kunden opkraeves for denne aendringsanmodning?
 *
 * Kun naar Orchestrator har klassificeret den som `out_of_scope`. Alt andet - en praecisering
 * i scope, en graazone, en klassifikation Orchestrator finder paa i morgen, eller en anmodning
 * der slet ikke kunne slaas op - er **ikke** penge. Timerne staar stadig paa bilaget, saa det
 * kan ses hvad der blev lavet; de er bare ikke i beloebet.
 *
 * Retningen er valgt med vilje: gaetter vi forkert her, koster det os en time, vi ikke fik
 * betalt. Gaetter vi forkert den anden vej, staar der en regning til kunden paa arbejde,
 * ingen har besluttet at opkraeve for. Samme regel som `changeRequestTotals` bruger paa
 * kontraktkortene, saa fakturaen og 'CR Sum' ikke kan sige hver sin ting.
 */
export function isChargeableChangeRequest(classification: string | null | undefined): boolean {
  return classification === BILLABLE_CR_CLASSIFICATION;
}

export const UNBILLABLE_REASONS = [
  'not_approved',
  'already_billed',
  'missing_task',
  'missing_rate',
] as const;
export type UnbillableReason = (typeof UNBILLABLE_REASONS)[number];

/** Danske forklaringer. Vises direkte i UI, saa controlleren kan handle paa dem. */
export const UNBILLABLE_REASON_TEXT: Record<UnbillableReason, string> = {
  not_approved: 'Timen er ikke godkendt endnu',
  already_billed: 'Timen er allerede faktureret',
  missing_task: 'Timen mangler en opgave og kan ikke grupperes',
  missing_rate: 'Timen mangler en timesats',
};

export interface UnbillableHour {
  hour_id: string;
  reason: UnbillableReason;
  duration_hours: number;
}

export interface PartitionedHours {
  billable: CandidateHour[];
  unbillable: UnbillableHour[];
}

/**
 * Deler kandidattimer i det der kan faktureres og det der ikke kan.
 *
 * Timer der ikke kan faktureres bliver ikke stiltiende udeladt - de returneres med en
 * aarsag, saa flowet kan vise "3 timer i perioden kan ikke faktureres" frem for at lade
 * controlleren opdage det en maaned senere.
 */
export function partitionHours(hours: readonly CandidateHour[]): PartitionedHours {
  const billable: CandidateHour[] = [];
  const unbillable: UnbillableHour[] = [];

  for (const hour of hours) {
    const reason = unbillableReason(hour);
    if (reason) {
      unbillable.push({ hour_id: hour.id, reason, duration_hours: hour.duration_hours });
    } else {
      billable.push(hour);
    }
  }

  return { billable, unbillable };
}

function unbillableReason(hour: CandidateHour): UnbillableReason | null {
  if (hour.already_billed) return 'already_billed';
  if (hour.validation_status !== 'approved') return 'not_approved';
  if (!hour.task_id) return 'missing_task';
  if (hour.hourly_rate_excl_vat === null || hour.hourly_rate_excl_vat <= 0) return 'missing_rate';
  return null;
}

export interface DraftInvoiceLine {
  task_id: string | null;
  /** Bliver invoice_lines.description og fryses der. */
  description: string;
  quantity: number;
  unit: Unit;
  unit_price_excl_vat: number;
  total_excl_vat: number;
  /** Timerne linjen daekker. Bliver ét invoice_billing_items pr. id. */
  hour_ids: string[];
}

/**
 * Grupperer fakturerbare timer til fakturalinjer - én linje pr. opgave.
 *
 * Grupperes paa opgave *og* timesats, ikke kun opgave. To timer paa samme opgave med
 * forskellig sats kan ikke deles om én linje, fordi linjen kun har én enhedspris; at
 * tvinge dem sammen ville aendre beloebet. I praksis er satsen ens inden for en kontrakt,
 * saa det giver normalt én linje pr. opgave - men naar satsen har aendret sig midt i et
 * forloeb, bliver det to linjer med samme titel, og totalen er stadig korrekt.
 */
export function groupHoursIntoLines(billable: readonly CandidateHour[]): DraftInvoiceLine[] {
  const groups = new Map<string, DraftInvoiceLine>();

  for (const hour of billable) {
    // partitionHours() har allerede afvist timer uden disse felter.
    const taskId = hour.task_id as string;
    const rate = hour.hourly_rate_excl_vat as number;
    const key = `${taskId}|${rate}`;

    const existing = groups.get(key);
    if (existing) {
      existing.quantity = round2(existing.quantity + hour.duration_hours);
      existing.total_excl_vat = lineTotal(existing.quantity, rate);
      existing.hour_ids.push(hour.id);
      continue;
    }

    groups.set(key, {
      task_id: taskId,
      description: hour.task_title ?? 'Opgave uden titel',
      quantity: round2(hour.duration_hours),
      unit: 'hours',
      unit_price_excl_vat: rate,
      total_excl_vat: lineTotal(hour.duration_hours, rate),
      hour_ids: [hour.id],
    });
  }

  // Stabil raekkefoelge: alfabetisk paa titel, saa preview og generering giver samme
  // linjenumre for samme input.
  return [...groups.values()].sort(
    (a, b) =>
      a.description.localeCompare(b.description, 'da') ||
      a.unit_price_excl_vat - b.unit_price_excl_vat,
  );
}

/**
 * Kontraktlinjenummeret ud af `todos.contract_line`, eller null naar raekken ikke peger paa
 * en linje.
 *
 * **Kolonnen er fri tekst, og formen er ikke ensartet i dataene.** Den samme betydning staar
 * som `'1'` paa ét board og `'01'` paa et andet, og nogle raekker baerer en tom streng frem
 * for null. Alle tre former skal give det samme svar, saa tolkningen hoerer ét sted - her -
 * frem for i hvert kaldested.
 *
 * Tolkningen er **snaever med vilje**. Kun hele, positive tal godtages; `'Linje 1'`, `'1.'`,
 * `'1-2'` og `'0'` giver null. Et loesere match ville laegge timer paa en anden fase, end den
 * de blev brugt paa, og det er kundens faktura, det gaar ud over. Kan vaerdien ikke laeses,
 * er det bedre at timen staar i samlegruppen, hvor et menneske kan se den.
 */
export function normalizeContractLine(value: string | null | undefined): number | null {
  const text = value?.trim();
  if (!text || !/^\d+$/.test(text)) return null;

  const parsed = Number.parseInt(text, 10);
  // `'0'` er ikke en linje - Dynamics' linjer taelles fra 1.
  return parsed > 0 ? parsed : null;
}

/**
 * The project agreement a service agreement task was worked on - see
 * `ContractLineTask.project_contract`.
 */
export interface TaskProjectContract {
  contract_no: string;
  /** The agreement's title in CRM. Null when the number is not one of the customer's agreements. */
  title: string | null;
}

/** Én opgave under en kontraktlinje. Bilagets mellemniveau. */
export interface ContractLineTask {
  task_id: string | null;
  title: string;
  /**
   * The project agreement whose board the task sits on, when it is service agreement work done
   * there. Only on a service agreement's line, and only when the board belongs to another
   * agreement than the service agreement itself.
   *
   * A task OnePro marks as 'Serviceaftale' is invoiced under the customer's service agreement
   * wherever it sits - see `splitHoursByContract` in the API - so this is what says which project
   * the work was done on. Shown next to the task on /fakturering/ny and on the bilag.
   *
   * Frozen with the line when the invoice is created (invariant 4). Optional, so lines stored
   * before the field existed read as they were.
   */
  project_contract?: TaskProjectContract | null;
  /**
   * Timerne der **faktureres**.
   *
   * Ikke altid de brugte: en buffer skalerer dem op (`applyInvoiceBuffer`), og controlleren kan
   * saette tallet i haanden (`applyTaskHours`). `used_hours` ved siden af er det, der faktisk er
   * registreret, og forskellen mellem de to er bufferen - se `derivedBuffer`.
   */
  quantity: number;
  /**
   * De timer, der er registreret paa opgaven i OnePro.
   *
   * Uroert af baade bufferen og en manuel retning, og det er hele nytten: uden den kunne
   * previewet ikke vise, hvad der blev lavet, ved siden af hvad der opkraeves - og bufferen
   * kunne ikke regnes ud af de to.
   *
   * **Valgfri, og falder tilbage paa `quantity`.** Flere veje bygger en opgave uden at kunne
   * skelne de to tal - den skemabaserede motor i invoice-service.ts har ingen buffer, og et
   * genskabt bilag har ingen opgaver - og dér *er* det opkraevede det arbejdede. Falder den
   * tilbage, bliver bufferandelen nul, hvilket er det rigtige svar, naar forskellen er ukendt.
   */
  used_hours?: number;
  /**
   * Opgavens estimat i timer - `todos.time_estimate`. Null naar ingen har sat et.
   *
   * Vises ved siden af de to andre paa /fakturering/ny, saa det kan ses, naar en buffer bringer
   * fakturaen over det, opgaven var sat til. Det er ikke forbudt at fakturere over estimatet -
   * derfor en advarsel og ingen blokering.
   *
   * Valgfri: kun OnePro-vejen kender feltet. Uden et estimat advares der ikke.
   */
  estimate_hours?: number | null;
  /**
   * Hvor mange timer bufferen havde lov at laegge paa opgaven: estimatet minus det, der i forvejen
   * faktureres. 0 naar opgaven er ved - eller over - sit estimat, og naar den intet estimat har.
   * Sat af `planInvoiceBuffer`; udeladt paa opgaver, bufferen ikke kigger paa.
   */
  buffer_room_hours?: number;
  /**
   * Antallet er sat i haanden af controlleren.
   *
   * En rettet opgave foelger ikke bufferen: bufferen fordeles paa de oevrige, og denne bliver
   * staaende paa det, der blev skrevet. Rækkefølgen er `applyInvoiceBuffer` foerst og
   * `applyTaskHours` derefter, saa den manuelle vaerdi vinder over den fordelte.
   */
  adjusted?: boolean;
  hour_ids: string[];
  /**
   * Underopgavernes navne - `todo_tasks.name` - og **kun** navnene.
   *
   * Uden tid, med vilje. En underopgave i OnePro baerer godt sin egen `total_time`, men den
   * ruller ikke op i foraeldrens `total_time`, og faktureringen regner kun paa foraeldren:
   * sub-tasks resolves til parent-opgaven ved synk, saa timen kun baerer opgave-referencen
   * (CLAUDE.md). Stod der et timetal her, ville bilaget laese som om de timer var med paa
   * fakturaen - og summen af niveauet ville ikke stemme med linjen ovenover.
   *
   * Fyldes af `subtaskNames` i demo/live-invoice-preview.ts. Den rene gruppering saetter en
   * tom liste: den laeser ingen database.
   */
  subtasks: string[];
  /**
   * Fravalgt af controlleren paa /fakturering/ny.
   *
   * En fravalgt opgave **bliver staaende** i svaret frem for at blive filtreret vaek, og det er
   * hele pointen: forsvandt raekken, forsvandt ogsaa fluebenet, og valget kunne ikke tages om.
   * Den taeller ikke i linjens `quantity` og kommer ikke med paa fakturaen - se `billableLines`,
   * som er det, der sendes til BC.
   */
  excluded: boolean;
  /** Opgaven kommer fra en aendringsanmodning. Kun de kan goeres gratis. */
  is_change_request: boolean;
  /**
   * Aendringen er daekket af det aftalte scope og opkraeves derfor ikke.
   *
   * Sat af `isChargeableChangeRequest` ud fra anmodningens klassifikation i Orchestrator, og
   * altsaa **ikke** et valg controlleren traeffer - deri ligger forskellen paa denne og `free`.
   * En `in_scope_clarification` er en praecisering af noget, kunden allerede har betalt for; en
   * `grey_zone` er uafklaret; og en anmodning, der ikke kunne slaas op, ved vi ingenting om.
   * Ingen af dem maa blive til et beloeb.
   *
   * Virkningen er den samme som paa en gratis opgave - timerne staar paa bilaget, beloebet er
   * nul, og timerne kommer i `hour_ids`, saa de er opkraevet én gang for alle - **paa naer to
   * ting**:
   *
   *   bufferen  en gratis aendring gives vaek og traekkes fra aftalens buffer, mens denne
   *             allerede er betalt af kontraktsummen. Se `freeChangeRequestShares`, som kun
   *             laeser `free`.
   *   kunden    denne staar paa det bilag, kunden faar - den forklarer en raekke uden beloeb -
   *             mens `free` holdes ude af det. Se `invoiceBasisGroups` i API'et.
   */
  covered_by_scope: boolean;
  /**
   * Gjort gratis af controlleren. **Kun muligt paa en aendringsanmodning, der kan opkraeves.**
   *
   * Forskellen paa gratis og fravalgt er, hvad der sker bagefter. En fravalgt opgave er ikke
   * faktureret og dukker op igen paa naeste faktura. En gratis opgave **er** faktureret - til
   * nul kroner: den kommer med i linjens `hour_ids`, taeller derfor som opkraevet og kommer
   * aldrig igen. Invariant 1 i CLAUDE.md gaelder ogsaa, naar prisen er nul.
   *
   * **Kunden faar aldrig at vide, at den var gratis.** Den bliver ikke til et beloeb paa
   * fakturaen, den staar ikke paa den kundevendte linje, og den naar heller ikke det bilag, der
   * vedhaeftes fakturaen - `invoiceBasisGroups` i API'et udelader flaget. At vi har givet
   * arbejde vaek er vores forhandlingsposition; skrev vi det paa regningen, ville den naeste
   * forhandling starte et andet sted. Maerkatet findes derfor kun paa /fakturering/ny, hvor
   * controlleren saetter det.
   *
   * Arbejdet betales i stedet af aftalens buffer, se `freeChangeRequestShares`.
   */
  free: boolean;
}

/**
 * Aftalen en fakturalinje hoerer til.
 *
 * Staar paa **linjen** og ikke kun paa fakturaen, fordi én faktura kan daekke flere aftaler:
 * linjerne grupperes pr. aftale i baade Faktura-preview og Bilag-preview, og grupperingen skal
 * kunne laves af linjerne alene - ogsaa naar de laeses tilbage fra Business Central, hvor der
 * ikke er noget aftalefelt.
 *
 * Null betyder "uden aftale": timer paa en tavle, der ikke hoerer til nogen kontrakt. De er en
 * gruppe paa lige fod med aftalerne og ikke en mangel.
 */
export interface InvoiceLineContract {
  id: string;
  contract_no: string | null;
  title: string;
  /**
   * Kontaktpersonen paa aftalen i CRM (`parentcontactid`) - 'Kontaktperson' under aftalens
   * overskrift paa bilaget. Samme person som `contact_name` paa fakturaens aftaler i
   * `InvoiceListItem`.
   *
   * Fryses paa linjen, naar fakturaen oprettes, som de andre kundevendte vaerdier (invariant 4):
   * et bilag til en sendt faktura maa ikke skifte navn, fordi kontakten rettes i CRM bagefter.
   * Valgfri, saa linjer gemt foer feltet fandtes, laeses som de var - uden navnet.
   */
  contact_name?: string | null;
  /**
   * Tavlerne i OnePro bag fakturaens opgaver - 'Projekt' under aftalens overskrift paa bilaget.
   * Kun paa en serviceaftale, og kun tavler med opgaver paa fakturaen.
   *
   * Fryses som `contact_name` (invariant 4): et bilag til en sendt faktura maa ikke skifte navn,
   * fordi tavlen omdoebes i OnePro bagefter. Valgfri, saa linjer gemt foer feltet fandtes, laeses
   * som de var.
   */
  board_names?: string[];
}

/**
 * En fakturalinje pr. kontraktlinje, med de opgaver den daekker.
 *
 * Samme felter som `DraftInvoiceLine` paa det kundevendte niveau, saa den kan blive en
 * BC-linje uaendret - plus `tasks`, som kun bilaget bruger, og `contract`, som kun
 * grupperingen bruger.
 */
export interface ContractLineInvoiceLine {
  /** Aftalen linjen hoerer til. Null for gruppen af timer uden aftale. */
  contract: InvoiceLineContract | null;
  /** Normaliseret linjenummer, eller null for samlegruppen og for aendringslinjen. */
  contract_line: number | null;
  description: string;
  quantity: number;
  unit: Unit;
  unit_price_excl_vat: number;
  total_excl_vat: number;
  hour_ids: string[];
  tasks: ContractLineTask[];
  /**
   * Linjen samler aendringsanmodninger frem for en kontraktlinje.
   *
   * UI bruger den til at vide, hvor 'Gratis' maa saettes. Den er ikke kundevendt - kunden ser
   * kun `description`.
   */
  is_change_request: boolean;
  /**
   * Linjen er en serviceaftales timer.
   *
   * Den slags linje roeres ikke af noget, controlleren kan skrue paa: ingen buffer, ingen timer
   * rettet i haanden og intet 'Gratis'. Antallet er de brugte timer paa opgaverne til aftalens
   * timepris - aftalens vilkaar, ikke en vurdering. Se `groupHoursForServiceAgreement`.
   *
   * Valgfri, saa linjer gemt foer feltet fandtes, laeses som det, de var: projektlinjer.
   */
  service_agreement?: boolean;
}

/** Teksten paa samlegruppen. Kundevendt, saa den staar paa dansk. */
export const NO_CONTRACT_LINE_LABEL = 'Uden kontraktlinje';

/**
 * Teksten paa aendringslinjen. Kundevendt, saa den staar paa dansk - jf. sprogkonventionen i
 * CLAUDE.md, hvor `source`-vaerdien 'Change Request' er data fra Orchestrator og ikke en label.
 */
export const CHANGE_REQUEST_LINE_LABEL = 'Ændringsanmodninger';

/**
 * Grupperer fakturerbare timer til én linje pr. **kontraktlinje** frem for pr. opgave.
 *
 * Det er den model fakturaen faktisk skal have: kunden ser fasen fra aftalen - `01 - Fundament`
 * - og ikke de fem opgaver, den blev delt op i internt. Opgaverne forsvinder ikke; de foelger
 * med i `tasks` og staar i bilaget.
 *
 * `titles` er kontraktlinjernes beskrivelser fra Dynamics, slaaet op paa deres normaliserede
 * nummer. Mangler nummeret i kortet, bruges `Kontraktlinje <n>`: linjen findes i timerne, men
 * ikke laengere i aftalen, og det maa ikke skjule timerne.
 *
 * **Timer uden en laesbar `contract_line` samles i én gruppe** med `contract_line: null`.
 * De udelades bevidst ikke: kolonnen er udfyldt paa et mindretal af raekkerne i dag, og en
 * faktura, der tabte de timer i stilhed, ville vaere vaerre end en, der viser dem samlet.
 *
 * Grupperes paa linje *og* timesats, af samme grund som `groupHoursIntoLines`: en linje har
 * kun én enhedspris, og at tvinge to satser sammen ville aendre beloebet.
 */
export function groupHoursByContractLine(
  billable: readonly CandidateHour[],
  titles: ReadonlyMap<number, string> = new Map(),
  excludedTaskIds: ReadonlySet<string> = new Set(),
  freeTaskIds: ReadonlySet<string> = new Set(),
): ContractLineInvoiceLine[] {
  const groups = new Map<string, ContractLineInvoiceLine>();

  for (const hour of billable) {
    // partitionHours() har allerede afvist timer uden sats.
    const rate = hour.hourly_rate_excl_vat as number;
    const isChangeRequest = hour.is_change_request === true;
    const isExcluded = hour.task_id !== null && excludedTaskIds.has(hour.task_id);
    /*
     * Daekket af scope: en aendring, Orchestrator ikke har klassificeret som `out_of_scope`.
     * Den staar paa bilaget med sine timer, men bliver aldrig til et beloeb - se
     * `ContractLineTask.covered_by_scope`.
     */
    const coveredByScope = isChangeRequest && !isChargeableChangeRequest(hour.cr_classification);
    /*
     * Aendringsanmodninger samles under deres egen linje, uanset hvad `todos.contract_line`
     * siger: de er ikke en fase i aftalen, men arbejde aftalt undervejs, og kunden skal kunne
     * se dem for sig. Nummeret bruges derfor kun til de oevrige timer.
     */
    const line = isChangeRequest ? null : normalizeContractLine(hour.contract_line);
    const key = `${isChangeRequest ? 'cr' : (line ?? 'none')}|${rate}`;
    /*
     * Kun en aendringsanmodning kan vaere gratis - se `ContractLineTask.free` - og kun en, der
     * overhovedet kunne opkraeves. At give en i-scope-aendring vaek er ikke en gave: kunden har
     * allerede betalt for den, og traekket fra bufferen ville vaere en udgift, ingen paadrog sig.
     */
    const isFree =
      isChangeRequest && !coveredByScope && hour.task_id !== null && freeTaskIds.has(hour.task_id);

    const group =
      groups.get(key) ??
      (() => {
        const created: ContractLineInvoiceLine = {
          // Aftalen kendes af kaldstedet, ikke af grupperingen. Se `withContract`.
          contract: null,
          contract_line: line,
          description: isChangeRequest
            ? CHANGE_REQUEST_LINE_LABEL
            : line === null
              ? NO_CONTRACT_LINE_LABEL
              : (titles.get(line) ?? `Kontraktlinje ${line}`),
          quantity: 0,
          unit: 'hours',
          unit_price_excl_vat: rate,
          total_excl_vat: 0,
          hour_ids: [],
          tasks: [],
          is_change_request: isChangeRequest,
        };
        groups.set(key, created);
        return created;
      })();

    /*
     * Kun de valgte timer taeller i linjens antal og beloeb. En fravalgt time er ikke en del af
     * fakturaen - men dens opgave staar stadig i `tasks` nedenfor, saa fluebenet kan saettes igen.
     *
     * En **gratis** time taeller heller ikke i antal eller beloeb - kunden betaler ikke for den -
     * men den kommer med i `hour_ids`. Det er forskellen paa gratis og fravalgt: den er
     * faktureret til nul kroner og maa aldrig komme igen. Se `ContractLineTask.free`.
     *
     * Det samme gaelder en aendring, der er **daekket af scope**: timerne staar paa bilaget og
     * er opkraevet én gang for alle, men beloebet er kontraktsummens og ikke fakturaens.
     */
    if (!isExcluded) {
      if (!isFree && !coveredByScope) {
        group.quantity = round2(group.quantity + hour.duration_hours);
        group.total_excl_vat = lineTotal(group.quantity, rate);
      }
      group.hour_ids.push(hour.id);
    }

    /*
     * Opgaven kan baere flere timer. De laegges sammen paa samme opgave, saa bilaget viser
     * ét tal pr. opgavenavn - det er den form, spoergsmaalet "hvor gik timerne hen" stilles i.
     * Timer uden opgave-id grupperes paa titlen, saa de ikke bliver én raekke hver.
     */
    const taskKey = hour.task_id ?? `title:${hour.task_title ?? ''}`;
    const task = group.tasks.find((row) => (row.task_id ?? `title:${row.title}`) === taskKey);

    if (task) {
      task.quantity = round2(task.quantity + hour.duration_hours);
      task.used_hours = task.quantity;
      /* Flere timer paa samme opgave: estimaterne er det samme felt, saa den foerste gaelder. */
      task.estimate_hours = task.estimate_hours ?? hour.estimate_hours ?? null;
      task.hour_ids.push(hour.id);
    } else {
      group.tasks.push({
        task_id: hour.task_id,
        title: hour.task_title ?? 'Opgave uden titel',
        quantity: round2(hour.duration_hours),
        /* De to er ens her. Bufferen og en manuel retning flytter kun den foerste. */
        used_hours: round2(hour.duration_hours),
        estimate_hours: hour.estimate_hours ?? null,
        adjusted: false,
        hour_ids: [hour.id],
        // Navnene slaas op i databasen og hoerer derfor ikke i en ren funktion - se feltet.
        subtasks: [],
        excluded: isExcluded,
        is_change_request: isChangeRequest,
        covered_by_scope: coveredByScope,
        free: isFree,
      });
    }
  }

  for (const group of groups.values()) {
    group.tasks.sort((a, b) => a.title.localeCompare(b.title, 'da'));
  }

  /*
   * Stabil raekkefoelge: linjenummeret, som aftalen laeses i - saa samlegruppen, og til sidst
   * aendringerne. De to sidste er ikke faser, men rester der skal ses efter de rigtige linjer,
   * og aendringerne staar nederst, fordi de er aftalt efter de oevrige.
   */
  const rank = (line: ContractLineInvoiceLine): number =>
    line.is_change_request ? 2 : line.contract_line === null ? 1 : 0;

  return [...groups.values()].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    if (a.contract_line === null || b.contract_line === null) {
      return a.unit_price_excl_vat - b.unit_price_excl_vat;
    }
    return a.contract_line - b.contract_line || a.unit_price_excl_vat - b.unit_price_excl_vat;
  });
}

/**
 * Grupperer fakturerbare timer til én linje pr. **opgave**, i samme form som
 * `groupHoursByContractLine`.
 *
 * Formen bruges af fakturaer **uden kontrakt**. Der findes da ingen kontraktlinjer at gruppere
 * under, og opgavetitlen er dermed det kundevendte niveau - faktureringsmodellen i CLAUDE.md,
 * hvor kunden kun ser opgavetitlen.
 *
 * `ContractLineInvoiceLine` og ikke `DraftInvoiceLine`, selvom linjen er pr. opgave: `tasks` er
 * det niveau fluebenene paa /fakturering/ny saetter, og uden det kunne timerne ikke vaelges til
 * og fra. Hver linje baerer derfor sin ene opgave i `tasks`.
 *
 * `contract_line` er null paa alle linjer. `todos.contract_line` peger paa en linje i en aftale,
 * og uden en aftale er tallet uden betydning - at bruge det ville navngive linjerne efter en
 * kontrakt, fakturaen ikke har.
 *
 * Grupperes paa opgave *og* sats, af samme grund som `groupHoursIntoLines`: en linje har kun én
 * enhedspris.
 */
export function groupHoursByTask(
  billable: readonly CandidateHour[],
  excludedTaskIds: ReadonlySet<string> = new Set(),
  freeTaskIds: ReadonlySet<string> = new Set(),
): ContractLineInvoiceLine[] {
  const groups = new Map<string, ContractLineInvoiceLine>();

  for (const hour of billable) {
    // partitionHours() har allerede afvist timer uden sats.
    const rate = hour.hourly_rate_excl_vat as number;
    const title = hour.task_title ?? 'Opgave uden titel';
    // Timer uden opgave-id grupperes paa titlen - samme noegle som groupHoursByContractLine.
    const taskKey = hour.task_id ?? `title:${title}`;
    const isExcluded = hour.task_id !== null && excludedTaskIds.has(hour.task_id);
    const isChangeRequest = hour.is_change_request === true;
    /*
     * Daekket af scope - samme regel som `groupHoursByContractLine`. Uden en aftale er der
     * sjaeldent en anmodning at slaa op, og en time uden svar er netop en, vi ikke ved om vi
     * maa opkraeve for. Se `ContractLineTask.covered_by_scope`.
     */
    const coveredByScope = isChangeRequest && !isChargeableChangeRequest(hour.cr_classification);
    /*
     * Gratis virker ogsaa her, men uden virkning paa nogen buffer: en faktura uden kontrakt har
     * ingen aftale at traekke fra - se `freeChangeRequestShares`, som springer dem over. Timen
     * er stadig faktureret til nul og kommer ikke igen.
     */
    const isFree =
      isChangeRequest && !coveredByScope && hour.task_id !== null && freeTaskIds.has(hour.task_id);

    const group =
      groups.get(`${taskKey}|${rate}`) ??
      (() => {
        const created: ContractLineInvoiceLine = {
          contract: null,
          contract_line: null,
          description: title,
          quantity: 0,
          unit: 'hours',
          unit_price_excl_vat: rate,
          total_excl_vat: 0,
          hour_ids: [],
          tasks: [
            {
              task_id: hour.task_id,
              title,
              quantity: 0,
              used_hours: 0,
              estimate_hours: hour.estimate_hours ?? null,
              adjusted: false,
              hour_ids: [],
              // Navnene slaas op i databasen og hoerer derfor ikke i en ren funktion.
              subtasks: [],
              excluded: isExcluded,
              is_change_request: isChangeRequest,
              covered_by_scope: coveredByScope,
              free: isFree,
            },
          ],
          is_change_request: isChangeRequest,
        };
        groups.set(`${taskKey}|${rate}`, created);
        return created;
      })();

    /*
     * Kun de valgte timer taeller i linjens antal og beloeb - men opgaven under den taeller dem
     * alle, saa bilaget viser hvad der blev fravalgt. Samme regel som groupHoursByContractLine,
     * inklusive at en gratis time - og en aendring daekket af scope - kommer i `hour_ids` men
     * ikke i beloebet.
     */
    if (!isExcluded) {
      if (!isFree && !coveredByScope) {
        group.quantity = round2(group.quantity + hour.duration_hours);
        group.total_excl_vat = lineTotal(group.quantity, rate);
      }
      group.hour_ids.push(hour.id);
    }

    const task = group.tasks[0] as ContractLineTask;
    task.quantity = round2(task.quantity + hour.duration_hours);
    task.used_hours = task.quantity;
    task.estimate_hours = task.estimate_hours ?? hour.estimate_hours ?? null;
    task.hour_ids.push(hour.id);
  }

  // Stabil raekkefoelge: alfabetisk paa titel, saa preview og oprettelse giver samme linjer.
  return [...groups.values()].sort(
    (a, b) =>
      a.description.localeCompare(b.description, 'da') ||
      a.unit_price_excl_vat - b.unit_price_excl_vat,
  );
}

/** Teksten over gruppen af linjer, der ikke hoerer til en aftale. Kundevendt, altsaa dansk. */
export const WITHOUT_CONTRACT_LABEL = 'Uden kontrakt';

/** Én gruppe linjer fra `groupLinesByContract`. */
export interface ContractLineGroup<T> {
  contract: InvoiceLineContract | null;
  /**
   * Om fakturaen **ved**, hvilken aftale linjerne hoerer til.
   *
   * `known: true` med `contract: null` betyder "timer uden aftale" - en gruppe paa lige fod med
   * aftalerne. `known: false` betyder "det kan vi ikke sige": en faktura oprettet direkte i
   * Business Central, eller en hvis sidespor er vaek efter en genstart.
   *
   * Forskellen er ikke kosmetisk. De to blev foer samlet i én gruppe, og resultatet var, at
   * arbejde paa en aftale stod under overskriften "Uden kontrakt" - altsaa en paastand om at
   * det ikke hoerte til nogen aftale. En ukendt gruppe skal staa **uden** overskrift.
   */
  known: boolean;
  rows: T[];
}

/**
 * Linjer samlet pr. aftale, i den raekkefoelge aftalerne foerst optraeder.
 *
 * Grupperingen bag "Kontraktoplysninger" i baade Faktura-preview og Bilag-preview, og bag
 * afsnittene i gennemgangen paa /fakturering/ny. Den staar her og ikke i en komponent, saa de
 * tre visninger ikke kan komme til at gruppere hver sin maade - og saa raekkefoelgen er den
 * samme, som linjerne blev sendt til Business Central i.
 *
 * **Tre slags grupper, ikke to:** én pr. aftale, én for timer uden aftale (`contract: null`), og
 * én for de linjer fakturaen ikke kan placere (`contract` udeladt). Se `known`.
 */
export function groupLinesByContract<T extends { contract?: InvoiceLineContract | null }>(
  rows: readonly T[],
): Array<ContractLineGroup<T>> {
  const groups = new Map<string, ContractLineGroup<T>>();

  for (const row of rows) {
    const known = row.contract !== undefined;
    const contract = row.contract ?? null;
    /* Tre noeglerum: aftalens id, den kendte tomme gruppe, og den ukendte. */
    const key = contract ? `id:${contract.id}` : known ? 'none' : 'unknown';

    const group = groups.get(key) ?? { contract, known, rows: [] };
    group.rows.push(row);
    groups.set(key, group);
  }

  return [...groups.values()];
}

/** Én gruppes linjer lagt sammen - se `summarizeInvoiceLines`. */
export interface InvoiceLineSummary {
  quantity: number;
  /** Null naar linjerne ikke deler enhed. */
  unit: string | null;
  /** Null naar linjerne ikke deler enhedspris - se nedenfor. */
  unit_price_excl_vat: number | null;
  total_excl_vat: number;
}

/**
 * Fakturalinjerne i én gruppe lagt sammen til ét saet tal.
 *
 * Faktura-previewet paa /fakturering/:id viser **én raekke pr. aftale**: kunden skal kunne se,
 * hvad aftalen koster, ikke hvordan arbejdet var delt op internt. Opdelingen er ikke vaek - den
 * staar i Bilag-previewet lige nedenunder, som netop er specifikationen.
 *
 * `unit_price_excl_vat` er **null**, naar linjerne ikke har den samme pris. To timesatser paa
 * den samme aftale har ingen faelles enhedspris, og at vise den ene ville saette et forkert tal
 * ved siden af en rigtig sum. Det samme gaelder enheden.
 *
 * Totalen er summen af linjernes egne totaler og ikke antal x pris: linjerne er allerede
 * afrundet hver for sig, og en genudregning kunne lande en oere fra fakturaens egen sum.
 */
export function summarizeInvoiceLines(
  lines: readonly {
    quantity: number;
    unit?: string | null;
    unit_price_excl_vat: number;
    total_excl_vat: number;
  }[],
): InvoiceLineSummary {
  const units = new Set(lines.map((line) => line.unit ?? null));
  const prices = new Set(lines.map((line) => line.unit_price_excl_vat));

  return {
    quantity: round2(lines.reduce((total, line) => total + line.quantity, 0)),
    unit: units.size === 1 ? ([...units][0] ?? null) : null,
    unit_price_excl_vat: prices.size === 1 ? ([...prices][0] ?? null) : null,
    total_excl_vat: sumMoney(lines.map((line) => line.total_excl_vat)),
  };
}

/** Én kundevendt fakturalinje - se `customerInvoiceLines`. */
export interface CustomerInvoiceLine {
  /** Aftalen linjen daekker. Null for timer uden aftale; se ogsaa `known`. */
  contract: InvoiceLineContract | null;
  /** False naar fakturaen ikke ved, hvilken aftale linjen hoerer til - se `groupLinesByContract`. */
  known: boolean;
  description: string;
  quantity: number;
  unit: string | null;
  unit_price_excl_vat: number;
  total_excl_vat: number;
}

/** Linjeteksten paa en aftale: `ONC-1011 · Keymate Portal`. Kundevendt, derfor dansk fallback. */
export function contractLineLabel(contract: InvoiceLineContract | null): string {
  if (!contract) return WITHOUT_CONTRACT_LABEL;
  return (
    [contract.contract_no, contract.title].filter(Boolean).join(' · ') || WITHOUT_CONTRACT_LABEL
  );
}

/** The project agreement next to a service agreement task: `ONC-1011 · Keymate Portal`. */
export function projectContractLabel(contract: TaskProjectContract): string {
  return [contract.contract_no, contract.title].filter(Boolean).join(' · ');
}

/**
 * Fakturaen som kunden faar den: **én linje pr. aftale.**
 *
 * Timerne er grupperet pr. kontraktlinje internt - `01 - Fundament`, `Uden kontraktlinje` - og
 * det er den opdeling, bilaget viser. Paa selve fakturaen er den stoej: kunden har én aftale med
 * os og skal se, hvad den aftale koster. Timer uden aftale samles paa samme maade under
 * `WITHOUT_CONTRACT_LABEL`.
 *
 * **Den samme funktion bag baade previewet og det, der sendes til Business Central.** BC's linjer
 * laeses tilbage og bliver til previewet, og de to skal vaere det samme - ikke to lister, der
 * regner hver for sig og kan naa hver sin faktura.
 *
 * **Én linje pr. sats, ikke pr. aftale for enhver pris.** Har en aftales timer to forskellige
 * satser, findes der ingen faelles enhedspris: et gennemsnit ville saette en pris paa kundens
 * faktura, som ingen har aftalt, og BC ganger selv antal med stykpris. De faa aftaler med to
 * satser faar derfor to linjer med samme tekst - beloebet er stadig rigtigt.
 *
 * Linjer, hvor aftalen er **ukendt** - en faktura oprettet direkte i BC, eller en hvis sidespor
 * er vaek efter en genstart - staar som de er. Der er ingen overskrift at samle dem under, og en
 * samlelinje uden navn ville vaere daarligere end de linjer, BC faktisk har.
 *
 * **Linjer uden antal springes over.** En linje paa nul timer er ikke en linje, kunden skal se:
 * den opstaar, naar alt paa den er gratis eller daekket af scope, og den staar i grundlaget
 * alene for at baere timerne til bilaget - se `billableLines`. Den maa ikke ende som en tom
 * raekke paa regningen, og den aendrer ingen sum, fordi dens beloeb er nul.
 */
export function customerInvoiceLines(
  lines: readonly {
    contract?: InvoiceLineContract | null;
    description: string;
    quantity: number;
    unit?: string | null;
    unit_price_excl_vat: number;
    total_excl_vat: number;
  }[],
): CustomerInvoiceLine[] {
  const result: CustomerInvoiceLine[] = [];

  for (const group of groupLinesByContract(lines.filter((line) => line.quantity > 0))) {
    if (!group.known) {
      for (const line of group.rows) {
        result.push({
          contract: null,
          known: false,
          description: line.description,
          quantity: round2(line.quantity),
          unit: line.unit ?? null,
          unit_price_excl_vat: line.unit_price_excl_vat,
          total_excl_vat: round2(line.total_excl_vat),
        });
      }
      continue;
    }

    /* Satsen er noeglen, og raekkefoelgen er den, satserne foerst optraeder i. */
    const byRate = new Map<number, typeof group.rows>();
    for (const line of group.rows) {
      const rows = byRate.get(line.unit_price_excl_vat) ?? [];
      rows.push(line);
      byRate.set(line.unit_price_excl_vat, rows);
    }

    for (const [rate, rows] of byRate) {
      const summary = summarizeInvoiceLines(rows);
      result.push({
        contract: group.contract,
        known: true,
        description: contractLineLabel(group.contract),
        quantity: summary.quantity,
        unit: summary.unit,
        unit_price_excl_vat: rate,
        total_excl_vat: summary.total_excl_vat,
      });
    }
  }

  return result;
}

/**
 * Which of `lines` each customer line was made of: one list of indexes per line
 * `customerInvoiceLines` returns, in the same order.
 *
 * The way back from a line in Business Central to the work behind it - a line the bookkeeping
 * removes from a draft is one customer line, and its hours are on the lines listed here. Same
 * grouping as `customerInvoiceLines` - lines without a quantity left out, one per contract and
 * rate, an unknown contract one per line - and a test holds the two to it.
 */
export function customerInvoiceLineSources(
  lines: readonly {
    contract?: InvoiceLineContract | null;
    quantity: number;
    unit_price_excl_vat: number;
  }[],
): number[][] {
  const indexed = lines
    .map((line, index) => ({ ...line, index }))
    .filter((line) => line.quantity > 0);
  const result: number[][] = [];

  for (const group of groupLinesByContract(indexed)) {
    if (!group.known) {
      for (const line of group.rows) result.push([line.index]);
      continue;
    }

    const byRate = new Map<number, number[]>();
    for (const line of group.rows) {
      const indexes = byRate.get(line.unit_price_excl_vat) ?? [];
      indexes.push(line.index);
      byRate.set(line.unit_price_excl_vat, indexes);
    }
    result.push(...byRate.values());
  }

  return result;
}

/**
 * Saetter aftalen paa en gruppe linjer.
 *
 * Grupperingsfunktionerne er rene og kender kun timerne; hvilken aftale de hoerer til, ved
 * kaldstedet. Det sker i et navngivet trin frem for i en `map` paa kaldstedet, saa feltet ikke
 * bliver glemt paa den ene af de to grupperinger - og saa der er ét sted at laese, at null
 * betyder "uden aftale".
 */
export function withContract(
  lines: readonly ContractLineInvoiceLine[],
  contract: InvoiceLineContract | null,
): ContractLineInvoiceLine[] {
  return lines.map((line) => ({ ...line, contract }));
}

/**
 * Linjerne som de skal faktureres: uden de fravalgte opgaver, og uden de linjer der intet har
 * tilbage.
 *
 * Previewet viser **alle** opgaver, ogsaa de fravalgte, saa et flueben kan saettes igen. Det,
 * der sendes til Business Central, maa derimod kun vaere det valgte - og de to former maa ikke
 * blive den samme liste ved et uheld. Derfor er skillelinjen en funktion med et navn frem for
 * et filter et sted i oprettelsen.
 *
 * En linje uden opgaver - et beloebsgrundlag, fx en delfaktura - beholdes paa sit antal, saa
 * den ikke falder ud sammen med de tomme.
 *
 * **En linje uden beloeb bliver staaende, saa laenge den har opgaver tilbage.** Gratis
 * aendringer og aendringer daekket af scope taeller ikke i antallet, men de er faktureret - til
 * nul kroner - og deres timer skal i `hour_ids`, ellers dukker de op igen paa naeste faktura.
 * Er *alle* aendringer i perioden i scope, staar 'Ændringsanmodninger' derfor paa nul timer og
 * skal alligevel gemmes: det er den linje, bilaget viser timerne under.
 *
 * Kunden ser den ikke. En nullinje naar aldrig Business Central - `customerInvoiceLines`
 * filtrerer den fra - saa der kommer ingen tom linje paa regningen at spoerge til.
 */
export function billableLines(
  lines: readonly ContractLineInvoiceLine[],
): ContractLineInvoiceLine[] {
  return lines
    .map((line) => ({ ...line, tasks: line.tasks.filter((task) => !task.excluded) }))
    .filter((line) => line.quantity > 0 || line.tasks.length > 0);
}

/** Resultatet af `applyInvoiceBuffer` - linjerne, faktoren og det beloeb der landede. */
export interface BufferedLines {
  lines: ContractLineInvoiceLine[];
  /**
   * Faktoren hver linjes antal er ganget med. 1 naar der ikke er nogen buffer at fordele.
   *
   * Den samme for alle linjer - deraf "samme faktor". Foelgen er, at bufferen fordeles efter
   * linjernes **beloeb**: en linje paa 20.000 kr faar fire gange saa meget som en paa 5.000.
   */
  factor: number;
  /**
   * Bufferen der faktisk landede paa linjerne.
   *
   * Ikke altid det beloeb, der blev bedt om. Antallet paa en linje er to decimaler, og en
   * hundrededel time er ikke et lille beloeb: ved 1.000 kr/t er ét trin 10 kr, saa en faktura
   * med tre linjer kan lande op til en snes kroner fra det, der blev skrevet. Trinnet er
   * fakturaens egen enhed og kan ikke goeres finere; derfor **vises** dette tal - baade paa
   * /fakturering/ny og som 'Buffer' paa fakturaen - saa det, controlleren ser, er det, kunden
   * faktisk faktureres.
   *
   * 0 naar der ikke var noget at fordele paa: ingen linjer, eller linjer uden beloeb, fordi
   * timeprisen mangler. En buffer kan ikke laegges oven paa nul.
   */
  applied_excl_vat: number;
}

/**
 * Lægger en buffer oven paa fakturalinjerne - **usynligt for kunden.**
 *
 * **Bruges ikke laengere af live-vejen.** /fakturering/ny, oprettelsen og den automatiske
 * fakturering bruger `planInvoiceBuffer` i contract-buffer.ts, som fordeler aftalens samlede
 * buffer inden for opgavernes estimater. Denne er den gamle fordeling med én faktor paa alle
 * linjer, uden lofter.
 *
 * Bufferen er et beloeb i kroner, controlleren skriver paa /fakturering/ny. Den bliver til
 * ekstra *antal* paa de linjer, der allerede er der: beloebet divideres med enhedsprisen, og de
 * timer, det giver, fordeles paa linjerne med den samme faktor. Kunden ser derfor de samme
 * linjer med de samme priser og et lidt hoejere timetal - ikke en linje der hedder "buffer".
 *
 * Med én timepris paa fakturaen er regnestykket praecis det, controlleren taenker: 5.000 kr ved
 * 1.000 kr/t er fem timer fordelt ud. Har linjerne hver sin pris - to aftaler med hver sin sats
 * paa den samme faktura - er "enhedsprisen" ikke ét tal, og faktoren er da den, der laegger
 * praecis bufferen til i alt. Det er den samme regel; den er bare skrevet paa beloeb frem for
 * paa timer, saa den ogsaa holder, naar priserne er forskellige.
 *
 * **Opgaverne under linjen skaleres med.** Bilaget paa /fakturering/:id skal kunne laegges
 * sammen: staar linjen paa 10 timer, mens opgaverne under den summer til 8,5, ser det ud som om
 * halvanden time mangler - og hverken linjen eller opgaverne er forkerte, det er bufferen, der
 * kun var lagt paa det ene niveau. De registrerede timer kan stadig laeses i panelet
 * Fakturagrundlag, som henter dem live fra OnePro. Se `bufferedTasks`.
 *
 * **Serviceaftalernes linjer faar ingen buffer.** De staar uroert igennem, og deres beloeb
 * taeller ikke med i det, bufferen fordeles efter: en serviceaftale fakturerer de brugte timer
 * til aftalens timepris, og det er aftalens vilkaar - se `groupHoursForServiceAgreement`. Har
 * fakturaen kun serviceaftaler, er der intet at fordele paa, og `applied_excl_vat` er 0.
 *
 * Ren funktion, saa previewet og oprettelsen regner det samme - se filens hovedkommentar.
 */
export function applyInvoiceBuffer(
  lines: readonly ContractLineInvoiceLine[],
  bufferExclVat: number,
): BufferedLines {
  const subtotal = sumMoney(
    lines.filter((line) => !line.service_agreement).map((line) => line.total_excl_vat),
  );

  /*
   * Ingen buffer, intet at fordele paa, eller et beloeb der ikke er et tal: linjerne gaar
   * uroert igennem. En negativ buffer afvises her frem for at traekke ned - et fradrag er en
   * anden beslutning end en buffer, og den hoerer ikke i dette felt.
   */
  if (!Number.isFinite(bufferExclVat) || bufferExclVat <= 0 || subtotal <= 0) {
    return { lines: [...lines], factor: 1, applied_excl_vat: 0 };
  }

  const factor = (subtotal + bufferExclVat) / subtotal;

  const buffered = lines.map((line) => {
    if (line.service_agreement) return line;

    const quantity = round2(line.quantity * factor);
    return {
      ...line,
      quantity,
      total_excl_vat: lineTotal(quantity, line.unit_price_excl_vat),
      tasks: bufferedTasks(line.tasks, factor, quantity),
    };
  });

  return {
    lines: buffered,
    factor,
    applied_excl_vat: round2(
      sumMoney(
        buffered.filter((line) => !line.service_agreement).map((line) => line.total_excl_vat),
      ) - subtotal,
    ),
  };
}

/**
 * Opgaverne under en linje, skaleret med linjens egen faktor.
 *
 * Uden det staar bilaget med en linje paa 10 timer og opgaver, der summer til 8,5 - og det
 * laeser som om halvanden time er forsvundet. Bufferen hoerer paa begge niveauer, naar de vises
 * sammen.
 *
 * **Fravalgte opgaver skaleres ikke.** De er ikke med paa fakturaen og taeller ikke i linjens
 * antal, saa en buffer paa dem ville laegge timer til noget, ingen betaler for.
 *
 * Afrundingen samles paa den stoerste opgave. Hver opgave rundes til to decimaler, og resten -
 * hoejst et par hundrededele - laegges dér, hvor den betyder mindst. Uden det kunne summen af
 * opgaverne lande en hundrededel fra linjen, og saa var vi tilbage ved det, der skulle loeses.
 */
/**
 * Opgaver, bufferen ikke roerer.
 *
 * Gratis og fravalgte opgaver er ikke en del af linjens antal, og at gange dem op ville lade
 * bilaget summe til mere end linjen. Bufferen hoerer paa det, kunden faktisk betaler for.
 *
 * Det samme gaelder aendringer daekket af scope - og her er der mere paa spil end en sum, der
 * ikke gaar op: deres timetal paa bilaget er det, der faktisk blev arbejdet, og et
 * bufferpaalaeg ville skrive flere timer paa en aendring, ingen har opkraevet for.
 *
 * `adjusted` er den fjerde: controlleren har skrevet et antal i haanden, og en fordeling oven
 * paa det ville flytte tallet, der netop blev valgt. Se `applyTaskHours`.
 */
function untouchedByBuffer(task: ContractLineTask): boolean {
  return task.excluded || task.free || task.covered_by_scope || task.adjusted === true;
}

/**
 * De timer, der er arbejdet paa opgaven.
 *
 * Falder tilbage paa det opkraevede antal, naar kilden ikke kender de to hver for sig - se
 * `ContractLineTask.used_hours`. Bufferandelen bliver da nul frem for `NaN`.
 */
function usedHoursOf(task: Pick<ContractLineTask, 'quantity' | 'used_hours'>): number {
  return task.used_hours ?? task.quantity;
}

function bufferedTasks(
  tasks: readonly ContractLineTask[],
  factor: number,
  lineQuantity: number,
): ContractLineTask[] {
  const scaled = tasks.map((task) => ({
    ...task,
    quantity: untouchedByBuffer(task) ? task.quantity : round2(task.quantity * factor),
  }));

  const billable = scaled.filter((task) => !untouchedByBuffer(task));
  if (billable.length === 0) return scaled;

  const residual = round2(
    lineQuantity - round2(billable.reduce((total, task) => total + task.quantity, 0)),
  );
  if (residual === 0) return scaled;

  const largest = billable.reduce((best, task) => (task.quantity > best.quantity ? task : best));
  largest.quantity = round2(largest.quantity + residual);

  return scaled;
}

/**
 * Timer, controlleren har sat i haanden, slaaet op paa opgavens id.
 *
 * Vaerdien er de timer, der skal **faktureres** for opgaven - ikke et paalaeg oven i. Er den
 * hoejere end de brugte, er forskellen buffer; er den lavere, er der givet noget vaek.
 */
export type TaskHourOverrides = Readonly<Record<string, number>>;

/**
 * Saetter de timer, controlleren har rettet, og regner linjerne om efter dem.
 *
 * **Koeres efter `applyInvoiceBuffer`,** og det er ikke vilkaarligt: bufferen er en fordeling
 * paa alle linjer, og en manuel retning er en beslutning om én opgave. Kommer retningen sidst,
 * vinder den - og bufferen fordeles paa de oevrige, som `untouchedByBuffer` sikrer ved at
 * springe de rettede over.
 *
 * Linjens antal og beloeb regnes om af opgaverne bagefter, saa de tre niveauer - opgave, linje,
 * faktura - ikke kan komme til at sige hver sit. Det gaelder ogsaa uden en eneste retning:
 * funktionen er da et no-op paa tallene, hvilket er den egenskab, der goer den sikker at have
 * i kaeden altid.
 *
 * **Fravalgte, gratis og scope-daekkede opgaver kan ikke rettes.** De taeller ikke i linjens
 * beloeb, saa et antal paa dem ville vaere timer, ingen betaler for - og for en gratis eller
 * i-scope-aendring er timetallet paa bilaget netop det, der blev arbejdet.
 *
 * Negative og ulaeselige vaerdier ses der bort fra. Et fradrag er en anden beslutning end en
 * retning, og den hoerer ikke i dette felt - samme forbehold som bufferen tager.
 *
 * **En serviceaftales timer kan ikke rettes.** Linjen gaar uroert igennem, ogsaa naar klienten
 * sender en retning paa en af dens opgaver: antallet er de brugte timer, og det er aftalens
 * vilkaar. /fakturering/ny viser ikke feltet paa de raekker; dette er vaernet bag.
 */
export function applyTaskHours(
  lines: readonly ContractLineInvoiceLine[],
  overrides: TaskHourOverrides,
): ContractLineInvoiceLine[] {
  const ids = Object.keys(overrides);
  if (ids.length === 0) return [...lines];

  return lines.map((line) => {
    if (line.service_agreement) return line;

    const tasks = line.tasks.map((task) => {
      /* Uden et id kan retningen ikke stiles til opgaven - se `hour.task_id` i grupperingen. */
      if (task.task_id === null) return task;

      /* Fravalgt, gratis eller daekket af scope: intet beloeb at rette paa - se doc'en. */
      if (task.excluded || task.free || task.covered_by_scope) return task;

      const wanted = overrides[task.task_id];
      if (wanted === undefined || !Number.isFinite(wanted) || wanted < 0) return task;

      return { ...task, quantity: round2(wanted), adjusted: true };
    });

    /*
     * Linjen er summen af de opgaver, der faktisk opkraeves. Den samme regel som
     * `groupHoursByContractLine` bruger, saa en linje uden retninger faar praecis det antal, den
     * havde.
     */
    const quantity = round2(
      tasks
        .filter((task) => !task.excluded && !task.free && !task.covered_by_scope)
        .reduce((total, task) => total + task.quantity, 0),
    );

    return {
      ...line,
      quantity,
      total_excl_vat: lineTotal(quantity, line.unit_price_excl_vat),
      tasks,
    };
  });
}

/**
 * Bufferen, som linjerne faktisk baerer: forskellen mellem det opkraevede og det arbejdede.
 *
 * **Et resultat og ikke et input.** Bufferen kan komme to veje - et beloeb, controlleren skriver
 * i feltet, og et antal timer, controlleren retter paa en opgave - og de to skal vises som ét
 * tal. Regnet af linjerne selv er der kun ét svar, uanset hvilken vej det kom.
 *
 * Kan vaere **negativ**: retter man en opgave ned under de brugte timer, er der givet arbejde
 * vaek. Det er en anden beslutning end en buffer, og den staar derfor med sit fortegn frem for
 * at blive klippet til nul.
 *
 * Kun opgaver, der opkraeves, taeller med. En gratis eller fravalgt opgave er ikke en buffer -
 * dens timer er der, men beloebet er nul, og `freeChangeRequestShares` er det sted, den slags
 * traekkes fra aftalen.
 *
 * Serviceaftalernes linjer springes over. Deres antal er de brugte timer og kan ikke rettes, saa
 * der er ingen forskel at regne som givet vaek.
 */
export function derivedBuffer(lines: readonly ContractLineInvoiceLine[]): number {
  let buffer = 0;

  for (const line of lines) {
    if (line.service_agreement) continue;

    for (const task of line.tasks) {
      if (task.excluded || task.free || task.covered_by_scope) continue;
      buffer += (task.quantity - usedHoursOf(task)) * line.unit_price_excl_vat;
    }
  }

  return round2(buffer);
}

/** Opgaver, hvor der faktureres flere timer end estimatet. Grundlaget for advarslen. */
export interface TaskOverEstimate {
  task_id: string | null;
  title: string;
  quantity: number;
  estimate_hours: number;
}

/**
 * Opgaverne der faktureres over deres estimat.
 *
 * **En advarsel og ikke en blokering.** Det er ikke forbudt at fakturere over et estimat - et
 * estimat er et estimat - men det er noget, nogen skal have set, foer fakturaen sendes. Derfor
 * en liste her og en besked i `warnings`, ikke en `blocker`.
 *
 * Opgaver uden estimat taeller ikke med: 'intet estimat' er ikke nul timer, og en advarsel om
 * hver af dem ville gøre listen ulaeselig og advarslen vaerdiloes.
 */
export function tasksOverEstimate(lines: readonly ContractLineInvoiceLine[]): TaskOverEstimate[] {
  const over: TaskOverEstimate[] = [];

  for (const line of lines) {
    /*
     * Ikke paa en serviceaftale: dens antal er de brugte timer og ikke et tal, nogen har skruet
     * paa. Advarslen findes for at fange en buffer eller en retning, der gik for langt.
     */
    if (line.service_agreement) continue;

    for (const task of line.tasks) {
      if (task.excluded || task.free || task.covered_by_scope) continue;

      /* Intet estimat er ikke nul timer - se `tasksOverEstimate`s doc. */
      const estimate = task.estimate_hours ?? null;
      if (estimate === null || estimate <= 0) continue;
      if (task.quantity <= estimate) continue;

      over.push({
        task_id: task.task_id,
        title: task.title,
        quantity: task.quantity,
        estimate_hours: estimate,
      });
    }
  }

  return over;
}

/** Opgaver, hvor der faktureres faerre timer end der er brugt. Grundlaget for advarslen. */
export interface TaskBelowUsed {
  task_id: string | null;
  title: string;
  quantity: number;
  used_hours: number;
}

/**
 * Faktureres opgaven under de timer, der er brugt paa den?
 *
 * Det sker kun ved en retning i haanden - bufferen er ikke-negativ og kan kun laegge timer paa.
 * Afrundet til to decimaler begge veje, saa en omregning fra sekunder, der ender paa 7,4999,
 * ikke advarer mod et antal paa 7,50.
 *
 * Eksporteret til /fakturering/ny, som advarer paa raekken, mens der tastes - foer retningen er
 * sendt. Samme regel som `tasksBelowUsed`, saa raekken og beskeden fra serveren er enige.
 */
export function isBelowUsedHours(task: Pick<ContractLineTask, 'quantity' | 'used_hours'>): boolean {
  return round2(task.quantity) < round2(usedHoursOf(task));
}

/**
 * Opgaverne der faktureres under de brugte timer.
 *
 * **En advarsel og ikke en blokering**, som `tasksOverEstimate`: det kan vaere helt bevidst at
 * lade kunden slippe for nogle timer - men forskellen er arbejde, der ikke bliver betalt, og det
 * skal vaere set, foer fakturaen sendes.
 *
 * Samme afgraensning som `tasksOverEstimate`: kun opgaver, der baerer et beloeb, og aldrig paa en
 * serviceaftale, hvor antallet er de brugte timer og ikke kan rettes.
 */
export function tasksBelowUsed(lines: readonly ContractLineInvoiceLine[]): TaskBelowUsed[] {
  const below: TaskBelowUsed[] = [];

  for (const line of lines) {
    if (line.service_agreement) continue;

    for (const task of line.tasks) {
      if (task.excluded || task.free || task.covered_by_scope) continue;
      if (!isBelowUsedHours(task)) continue;

      below.push({
        task_id: task.task_id,
        title: task.title,
        quantity: task.quantity,
        used_hours: usedHoursOf(task),
      });
    }
  }

  return below;
}

/** Bufferens andel af én aftale paa fakturaen. Se `bufferSharesByContract`. */
export interface ContractBufferShare {
  /** Null for gruppen af timer uden aftale - dens andel hoerer ikke paa nogen kontrakt. */
  contract: InvoiceLineContract | null;
  amount_excl_vat: number;
}

/**
 * Bufferens fordeling paa fakturaens aftaler.
 *
 * En faktura kan daekke flere aftaler, og /kontrakter/:id viser bufferen pr. aftale. Andelen
 * foelger den samme noegle, som bufferen blev lagt paa med: linjernes **beloeb**. Det er ikke
 * et valg - bufferen *er* fordelt paa den maade af `applyInvoiceBuffer`, saa enhver anden
 * fordeling her ville vise noget andet, end der staar paa linjerne.
 *
 * Summen af andelene er bufferen, paa nær afrunding pr. aftale.
 *
 * Serviceaftalerne er ikke med: de faar ingen buffer - se `applyInvoiceBuffer` - og en andel paa
 * dem ville staa paa en aftale, hvis linjer bufferen aldrig roerte.
 */
export function bufferSharesByContract(
  lines: readonly ContractLineInvoiceLine[],
  bufferExclVat: number,
): ContractBufferShare[] {
  const buffered = lines.filter((line) => !line.service_agreement);
  const total = sumMoney(buffered.map((line) => line.total_excl_vat));
  if (bufferExclVat <= 0 || total <= 0) return [];

  return groupLinesByContract(buffered).map((group) => ({
    contract: group.contract,
    amount_excl_vat: round2(
      (sumMoney(group.rows.map((line) => line.total_excl_vat)) / total) * bufferExclVat,
    ),
  }));
}

/**
 * Vaerdien af det gratis arbejde pr. aftale - det bufferen skal betale.
 *
 * En aendringsanmodning, controlleren har sat til gratis, faktureres til nul kroner, men
 * arbejdet er lavet og koster os det samme som ellers. Beloebet traekkes derfor fra aftalens
 * buffer-pulje i `public.codepass_contract_buffer` - se `subtractContractBuffers` i API'et.
 * Fakturaen selv er uaendret; kunden ser hverken linjen eller traekket.
 *
 * **Regnet paa opgavens egne timer gange linjens sats** og ikke paa linjens beloeb: den gratis
 * opgave er netop ikke i linjens beloeb - det er hele pointen - saa der er intet at tage en
 * andel af. Bufferen er derfor det, arbejdet ville have kostet kunden.
 *
 * Fravalgte opgaver taeller ikke med: de er ikke faktureret og er ikke givet vaek, de kommer
 * igen paa naeste faktura. Timer uden aftale springes over af `subtractContractBuffers` - der er
 * ingen aftale at traekke fra - men de staar med her, saa den, der kalder, kan sige det.
 */
export function freeChangeRequestShares(
  lines: readonly ContractLineInvoiceLine[],
): ContractBufferShare[] {
  const byContract = new Map<string, ContractBufferShare>();

  for (const line of lines) {
    const free = line.tasks.filter((task) => task.free && !task.excluded);
    if (free.length === 0) continue;

    const amount = sumMoney(free.map((task) => lineTotal(task.quantity, line.unit_price_excl_vat)));
    if (amount <= 0) continue;

    const key = line.contract?.id ?? 'none';
    const current = byContract.get(key);

    if (current) current.amount_excl_vat = round2(current.amount_excl_vat + amount);
    else byContract.set(key, { contract: line.contract, amount_excl_vat: amount });
  }

  return [...byContract.values()];
}

/** Hvad en aftales buffer kan baere, og hvad der allerede er lagt paa den - se `bufferRoom`. */
export interface ContractBufferRoom {
  /** Null for gruppen uden aftale. Den har ingen buffer og kan derfor intet give vaek. */
  contract: InvoiceLineContract | null;
  /** Aftalens buffer, som den vil staa **efter** denne faktura er oprettet. */
  available_excl_vat: number;
  /** Vaerdien af det, der allerede er sat til gratis paa fakturaen. */
  given_excl_vat: number;
  /** Tilbage naar det bortgivne er trukket fra. Negativ betyder, at der er givet for meget vaek. */
  remaining_excl_vat: number;
}

/**
 * Hvor meget hver aftale har tilbage at give vaek af.
 *
 * Gratis er ikke en gave af huset - den betales af den buffer, aftalen har samlet op. Der er
 * derfor et loft, og det er dette: giver man mere vaek, end aftalen har taget ind, skriver man
 * et underskud paa en aftale under paaskud af at vaere large. Reglen staar her frem for i
 * knappen, saa /fakturering/ny og oprettelsen ikke kan naa hver sin konklusion - klienten skjuler
 * knappen, serveren afviser fakturaen, og begge regner det samme.
 *
 * `available` er **efter** fakturaen, ikke foer den: `createInBusinessCentral` laegger fakturaens
 * egen buffer paa aftalen, foer den traekker det bortgivne fra, saa et paalaeg skrevet i samme
 * arbejdsgang kan finansiere en gratis aendring. Kaldstedet laegger derfor `bufferSharesByContract`
 * oven i det gemte tal, foer det kaldes her. Gjorde vi det modsat, ville knappen vaere graa for
 * noget, der ville lykkes et sekund senere.
 *
 * **Én raekke pr. aftale paa fakturaen** - ogsaa dem, der ikke har givet noget vaek endnu. Det er
 * netop dem, knappen skal spoerge om: "er der plads til den foerste?". Var svaret kun de aftaler,
 * der allerede havde bortgivet arbejde, ville knappen aldrig kunne blive synlig til at begynde
 * med.
 *
 * Gruppen uden aftale staar med `available: 0` - den har ingen pulje at traekke fra (se
 * `subtractContractBuffers`), saa alt bortgivet dér er over loftet, og det skal kunne siges.
 */
export function bufferRoom(
  lines: readonly ContractLineInvoiceLine[],
  availableByContract: ReadonlyMap<string, number>,
): ContractBufferRoom[] {
  /* Det bortgivne slaas op pr. aftale - samme noegle som `freeChangeRequestShares` bruger. */
  const given = new Map(
    freeChangeRequestShares(lines).map((share) => [
      share.contract?.id ?? 'none',
      share.amount_excl_vat,
    ]),
  );

  return groupLinesByContract(lines).map((group) => {
    const available = group.contract
      ? (availableByContract.get(group.contract.id) ?? 0)
      : /* Uden aftale er der ingen pulje - hverken at laese eller at traekke fra. */ 0;
    const used = given.get(group.contract?.id ?? 'none') ?? 0;

    return {
      contract: group.contract,
      available_excl_vat: round2(available),
      given_excl_vat: used,
      remaining_excl_vat: round2(available - used),
    };
  });
}

/**
 * De aftaler, der har faaet givet mere vaek, end deres buffer kan baere.
 *
 * Tom liste er det normale og betyder "det gaar". Bruges baade til blockeren paa previewet og
 * til den 422, der standser oprettelsen - se `invoiceResponse` i API'et.
 */
export function overdrawnBuffers(
  lines: readonly ContractLineInvoiceLine[],
  availableByContract: ReadonlyMap<string, number>,
): ContractBufferRoom[] {
  return bufferRoom(lines, availableByContract).filter((row) => row.remaining_excl_vat < 0);
}

/**
 * Maa denne opgave goeres gratis? Hele reglen, ét sted.
 *
 * Fire betingelser, og de tre foerste er ikke om penge:
 *
 *   aendringsanmodning  kun de kan gives vaek. Almindeligt arbejde er aftalt og betalt.
 *   ikke i scope        en i-scope-aendring er allerede gratis for kunden; et buffertraek oven
 *                       i ville betale for den to gange.
 *   ikke fravalgt       en fravalgt opgave er slet ikke paa fakturaen, og to modsatrettede valg
 *                       paa den samme raekke ville forvirre.
 *   daekning            og saa er der pengene: bufferen skal kunne baere det, opgaven ville have
 *                       kostet kunden.
 *
 * **En opgave, der allerede er gratis, svares altid ja.** Dens vaerdi er med i `given` og
 * dermed traukket fra `remaining`, saa et rent regnestykke ville svare nej og lade knappen
 * forsvinde i det oejeblik, den blev trykket - valget kunne ikke tages om igen.
 *
 * Bruges af knappen paa /fakturering/ny; serveren haandhaever den samme graense med
 * `overdrawnBuffers` paa det samlede resultat. Klienten skjuler, serveren afviser.
 */
export function canGiveAway(
  task: Pick<
    ContractLineTask,
    'quantity' | 'free' | 'excluded' | 'is_change_request' | 'covered_by_scope'
  >,
  unitPriceExclVat: number,
  room: Pick<ContractBufferRoom, 'remaining_excl_vat'> | undefined,
): boolean {
  if (!task.is_change_request || task.covered_by_scope || task.excluded) return false;
  if (task.free) return true;

  return (room?.remaining_excl_vat ?? 0) >= lineTotal(task.quantity, unitPriceExclVat);
}

export interface InvoiceTotals {
  amount_excl_vat: number;
  vat_amount: number;
  amount_incl_vat: number;
  vat_rate: number;
  reverse_charge: boolean;
}

/**
 * Totaler ud fra linjerne - ikke ud fra timerne.
 *
 * Rundingen sker pr. linje, og totalen er summen af de afrundede linjer. Det er den
 * eneste maade fakturaens total kan afstemmes mod det kunden ser paa linjerne.
 */
export function calculateInvoiceTotals(
  lines: readonly Pick<DraftInvoiceLine, 'total_excl_vat'>[],
  options: { countryCode?: string; vatRate?: number } = {},
): InvoiceTotals {
  const amountExclVat = sumMoney(lines.map((line) => line.total_excl_vat));
  const vat: VatResult = calculateVat(amountExclVat, options);

  return {
    amount_excl_vat: amountExclVat,
    vat_amount: vat.vatAmount,
    amount_incl_vat: vat.amountInclVat,
    vat_rate: vat.vatRate,
    reverse_charge: vat.reverseCharge,
  };
}

/**
 * Taeller fakturaen som faktureret?
 *
 * Kun det, der har forladt Codepass - `sent_to_bc` og `posted`, se BILLED_INVOICE_STATUSES.
 * En kladde er ikke et krav paa kunden: den er en beslutning, der ikke er truffet endnu, og
 * den hoerer paa /fakturering. Samme regel som routes/contracts.ts og routes/customers.ts har
 * haft hele tiden; predikatet ligger her, saa 'Faktureret' paa /kontrakter og 'Faktureret i
 * aar' paa /kunder ikke kan komme til at betyde to forskellige ting.
 *
 * En BC-faktura, der bogfoeres, kommer tilbage som `Open`, `Paid` eller `Corrective`, og alle
 * tre oversaettes til `posted` (se STATUS_BY_BC) - saa den taeller med ved naeste sidevisning,
 * uden at der skal aendres kode.
 *
 * `unknown` frem for `InvoiceStatus`, saa den ogsaa kan bruges paa raekker der kommer ud af et
 * JSON-svar uden at vaere indsnaevret endnu.
 */
export function countsAsBilled(status: unknown): boolean {
  return BILLED_INVOICE_STATUSES.includes(status as InvoiceStatus);
}

/**
 * The payment terms of a customer whose own cannot be read from Business Central: 8 days, like
 * every other customer there (decided 08-10-2026; it was 30, the old schema's default). Shown with
 * the reason on /kunder, and a warning on the invoice - see `PaymentTermsIssue`.
 */
export const DEFAULT_PAYMENT_TERMS_DAYS = 8;

/**
 * Why a customer's payment terms are `DEFAULT_PAYMENT_TERMS_DAYS` rather than their own:
 *
 *   missing     none set on the customer card in Business Central
 *   unknown     set to a payment term BC does not have
 *   unreadable  the term's due date formula cannot be turned into days
 */
export type PaymentTermsIssue = 'missing' | 'unknown' | 'unreadable';

/** Forfaldsdato = fakturadato + kundens betalingsbetingelser. Snapshottes paa fakturaen. */
export function calculateDueDate(invoiceDate: string | Date, paymentTermsDays: number): string {
  if (!Number.isInteger(paymentTermsDays) || paymentTermsDays < 0) {
    throw new Error(`Ugyldige betalingsbetingelser: ${paymentTermsDays}`);
  }
  return addDays(invoiceDate, paymentTermsDays);
}

/** Beskrivelse paa recurring-fakturalinjen: "Abonnement april 2026". */
export function recurringLineDescription(monthYear: string): string {
  return `Abonnement ${monthYear}`;
}

/** Vinduet DSO-estimatet ser tilbage over. Samme paa dashboardet og pr. kunde. */
export const DSO_WINDOW_DAYS = 90;

export interface DsoInvoice {
  amount_excl_vat: number;
  /** Fakturadato. Tidsstempel eller ISO-dato - kun datodelen bruges. */
  created_at: string;
  due_date: string | null;
}

/**
 * DSO-estimat uden bankdata: den beloebsvaegtede gennemsnitlige kredittid paa de fakturaer,
 * der er sendt inden for vinduet.
 *
 * **Det er ikke betalingsadfaerd - det er betalingsbetingelser.** Antagelsen "kunden betaler
 * paa forfaldsdagen" er det bedste der kan udledes, saa laenge Codepass ikke ved hvornaar
 * der bliver betalt: en kunde der altid betaler 20 dage for sent, faar samme tal som en der
 * betaler til tiden. Derfor skal tallet altid vises maerket "estimeret" - se OQ-10, og
 * `customerHealth` for samme forbehold paa kundestatusen.
 *
 * Returnerer null naar der ikke er noget at regne paa, saa UI'et kan vise "—" frem for et 0
 * der ligner en maaling.
 */
export function estimateDsoDays(
  invoices: readonly DsoInvoice[],
  options: { today: string | Date; windowDays?: number },
): number | null {
  const cutoff = addDays(options.today, -(options.windowDays ?? DSO_WINDOW_DAYS));
  const recent = invoices.filter(
    (invoice) => invoice.due_date !== null && invoice.created_at.slice(0, 10) >= cutoff,
  );
  if (recent.length === 0) return null;

  const totalAmount = sumMoney(recent.map((invoice) => Number(invoice.amount_excl_vat)));
  if (totalAmount === 0) return null;

  const weightedDays = recent.reduce((sum, invoice) => {
    const days = daysBetween(invoice.created_at.slice(0, 10), invoice.due_date as string);
    return sum + days * Number(invoice.amount_excl_vat);
  }, 0);

  return round2(weightedDays / totalAmount);
}
